(function bootstrapSwyshServiceFee() {
  if (window.SwyshServiceFeeReady) return;

  const INSTALLATION_KEY = '__SwyshServiceFeeInstalled';
  const configElement = document.getElementById('swysh-service-fee-config');
  const scriptElement =
    document.currentScript ||
    document.querySelector('script[data-swysh-service-fee]');

  function failClosed(message) {
    document.querySelectorAll('[data-swysh-checkout]').forEach((element) => {
      element.setAttribute('aria-disabled', 'true');
      if (element instanceof HTMLButtonElement) element.disabled = true;
    });
    const cartError = document.getElementById('cart-errors');
    if (cartError && message) cartError.textContent = message;
  }

  let config;
  try {
    config = JSON.parse(configElement?.textContent || '');
  } catch {
    failClosed('Cart error');
    window.SwyshServiceFeeReady = Promise.reject(
      new Error('SERVICE_FEE_BOOTSTRAP_FAILED')
    );
    return;
  }

  const injectedCore = window.__SwyshServiceFeeCoreForTests;
  const coreUrl = scriptElement?.dataset.coreUrl;
  const corePromise = injectedCore
    ? Promise.resolve(injectedCore)
    : coreUrl
    ? import(coreUrl)
    : Promise.reject(new Error('SERVICE_FEE_CORE_URL_MISSING'));

  window.SwyshServiceFeeReady = corePromise
    .then((core) => createController(core, config))
    .then(async (controller) => {
      controller.install();
      await controller.initialize();
      return controller;
    })
    .catch((error) => {
      failClosed(config.errorMessage);
      throw error;
    });

  function createController(core, controllerConfig) {
    const eligibleProductIds = (controllerConfig.eligibleProductIds || []).map(
      Number
    );
    const routes = controllerConfig.routes || core.buildCartRoutes('/');
    const requestDeadlineMs = Number(controllerConfig.deadlineMs) || 10_000;
    const requestTimeoutMs =
      Number(controllerConfig.requestTimeoutMs) ||
      Math.min(5_000, requestDeadlineMs);
    const lock = core.createSubmissionLock();

    function now() {
      return Date.now();
    }

    async function requestJson(
      url,
      options = {},
      timeoutMs = requestTimeoutMs
    ) {
      const abortController = new AbortController();
      const timeoutId = window.setTimeout(
        () => abortController.abort(),
        Math.max(1, timeoutMs)
      );
      try {
        const response = await window.fetch(url, {
          credentials: 'same-origin',
          ...options,
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error('SERVICE_FEE_REQUEST_FAILED');
        }
        try {
          return await response.json();
        } catch {
          throw new Error('SERVICE_FEE_RESPONSE_INVALID');
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          const timeoutError = new Error('SERVICE_FEE_REQUEST_TIMEOUT');
          timeoutError.retryable = true;
          throw timeoutError;
        }
        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    function getCart(timeoutMs) {
      return requestJson(
        routes.cart,
        {
          method: 'GET',
          headers: { Accept: 'application/json' },
        },
        timeoutMs
      );
    }

    function addItems(items, timeoutMs) {
      return requestJson(
        routes.add,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        },
        timeoutMs
      );
    }

    function changeLine(key, quantity, properties, timeoutMs) {
      const body = { id: key, quantity };
      if (properties) body.properties = properties;
      return requestJson(
        routes.change,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
        timeoutMs
      );
    }

    async function reconcile() {
      const startedAt = now();
      let mutations = 0;

      function remainingRequestTime() {
        return Math.max(
          1,
          Math.min(requestTimeoutMs, requestDeadlineMs - (now() - startedAt))
        );
      }

      while (now() - startedAt <= requestDeadlineMs) {
        const freshCart = await getCart(remainingRequestTime());
        const action = core.planReconciliation(freshCart, {
          eligibleProductIds,
        });
        if (action.type === 'verified') {
          return {
            cart: freshCart,
            mutations,
            classification: action.classification,
          };
        }
        if (action.type === 'add') {
          await addItems(action.items, remainingRequestTime());
        } else if (action.type === 'change') {
          await changeLine(
            action.key,
            action.quantity,
            undefined,
            remainingRequestTime()
          );
        } else {
          throw new Error('SERVICE_FEE_RECONCILIATION_INVALID');
        }
        mutations += 1;
      }

      const error = new Error('SERVICE_FEE_RECONCILIATION_DEADLINE');
      error.retryable = true;
      throw error;
    }

    function setCheckoutEnabled(enabled) {
      document.querySelectorAll('[data-swysh-checkout]').forEach((element) => {
        element.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        if (element instanceof HTMLButtonElement) element.disabled = !enabled;
      });
    }

    function setButtonBusy(button, busy) {
      if (!button) return;
      button.classList.toggle('loading', busy);
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      button.disabled = busy;
    }

    function showError(form) {
      const errorMessage = controllerConfig.errorMessage || 'Cart error';
      const formError = form?.querySelector('[data-swysh-booking-error]');
      const errorElement = formError || document.getElementById('cart-errors');
      if (errorElement) {
        errorElement.textContent = errorMessage;
        errorElement.removeAttribute('hidden');
      }
    }

    function clearError(form) {
      const formError = form?.querySelector('[data-swysh-booking-error]');
      const errorElement = formError || document.getElementById('cart-errors');
      if (errorElement) {
        errorElement.textContent = '';
        if (formError) formError.setAttribute('hidden', 'hidden');
      }
    }

    function createOperationId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `swysh-${now()}-${Math.random().toString(36).slice(2)}`;
    }

    function formProperties(form) {
      const properties = {};
      new FormData(form).forEach((value, key) => {
        const match = /^properties\[(.+)]$/.exec(key);
        if (match) properties[match[1]] = String(value);
      });
      return properties;
    }

    function formBookingItem(form, trigger) {
      const data = new FormData(form);
      const properties = formProperties(form);
      if (trigger?.dataset.fanPass === 'true') properties._FanPass = 'Yes';
      const linkedCart =
        form.dataset.linkedCartId ||
        properties[core.LINKED_CART_PROPERTY] ||
        form.dataset.swyshOperationId;
      if (linkedCart) properties[core.LINKED_CART_PROPERTY] = linkedCart;

      return {
        id: Number(data.get('id')),
        product_id: Number(form.dataset.productId),
        quantity: Number(data.get('quantity')) || 1,
        properties,
      };
    }

    function findOperationLine(cart, operationId) {
      return (cart?.items || []).find(
        (item) =>
          String(item.properties?.[core.ADD_OPERATION_PROPERTY] || '') ===
          String(operationId)
      );
    }

    function wait(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }

    async function recoverAmbiguousAdd(operationId, startedAt) {
      while (now() - startedAt <= requestDeadlineMs) {
        const remainingMs = Math.max(
          1,
          requestDeadlineMs - (now() - startedAt)
        );
        const freshCart = await getCart(
          Math.min(requestTimeoutMs, remainingMs)
        );
        const resolution = core.resolveAmbiguousAdd({
          cart: freshCart,
          operationId,
          now: now(),
          startedAt,
          deadlineMs: requestDeadlineMs,
        });
        if (resolution.status === 'committed') return resolution.line;
        await wait(Math.min(250, remainingMs));
      }
      throw new Error('SERVICE_FEE_ADD_UNCONFIRMED');
    }

    function selectedAddOns(form, linkedCart) {
      return Array.from(form.querySelectorAll('.add-on-wrapper.selected')).map(
        (wrapper) => {
          const item = wrapper.querySelector('.add-on-item');
          const properties = {
            [core.LINKED_CART_PROPERTY]: linkedCart,
            _Addon: 'Yes',
            _Merch: item?.dataset.merch === 'true' ? 'Yes' : 'No',
          };
          if (item?.dataset.fanPass === 'true') properties._FanPass = 'Yes';
          return {
            id: Number(item?.dataset.addonVariantid),
            quantity: 1,
            properties,
          };
        }
      );
    }

    async function addMissingAddOns(form, linkedCart) {
      const desired = selectedAddOns(form, linkedCart).filter(
        (item) => item.id
      );
      if (desired.length === 0) return;
      const freshCart = await getCart();
      const existingVariantIds = new Set(
        (freshCart.items || [])
          .filter(
            (item) =>
              item.properties?._Addon === 'Yes' &&
              item.properties?.[core.LINKED_CART_PROPERTY] === linkedCart
          )
          .map((item) => Number(item.variant_id || item.id))
      );
      const missing = desired.filter(
        (item) => !existingVariantIds.has(item.id)
      );
      if (missing.length > 0) await addItems(missing);
    }

    async function preserveBookingIntegrations(email, bookingLine, linkedCart) {
      const integrations = window.SwyshBookingIntegrations;
      if (email && typeof integrations?.identify === 'function') {
        await Promise.resolve(integrations.identify(email)).catch(
          () => undefined
        );
      }
      if (email && routes.update) {
        await requestJson(routes.update, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ attributes: { email } }),
        });
      }
      if (bookingLine && typeof integrations?.track === 'function') {
        const item = {
          ProductID: String(bookingLine.product_id || ''),
          SKU: bookingLine.sku || '',
          ProductName: bookingLine.product_title || '',
          Quantity: bookingLine.quantity || 1,
          ItemPrice: Number(bookingLine.price || 0) / 100,
          RowTotal: Number(bookingLine.final_line_price || 0) / 100,
          ProductURL: bookingLine.url || '',
          ImageURL: bookingLine.image || '',
          ProductCategories: [],
        };
        await Promise.resolve(
          integrations.track('Added to Cart', {
            $value: item.RowTotal,
            ProductName: item.ProductName,
            ProductType: bookingLine.product_type || '',
            Brand: bookingLine.vendor || '',
            ProductID: item.ProductID,
            ProductURL: item.ProductURL,
            ImageURL: item.ImageURL,
            ItemNames: [item.ProductName],
            Items: [item],
            _linkedCart: linkedCart,
          })
        ).catch(() => undefined);
      }
    }

    async function addBooking(form, trigger) {
      const operationId = form.dataset.swyshOperationId || createOperationId();
      form.dataset.swyshOperationId = operationId;
      const initialCart = await getCart();
      let bookingLine = findOperationLine(initialCart, operationId);
      const bookingItem = formBookingItem(form, trigger);
      const linkedCart =
        bookingItem.properties[core.LINKED_CART_PROPERTY] || operationId;
      bookingItem.properties[core.LINKED_CART_PROPERTY] = linkedCart;

      if (!bookingLine) {
        const payload = core.buildBookingAndFeePayload({
          bookingItem,
          cart: initialCart,
          eligibleProductIds,
          operationId,
        });
        const startedAt = now();
        try {
          const response = await addItems(payload.items);
          const returnedItems = Array.isArray(response.items)
            ? response.items
            : [response];
          bookingLine = returnedItems.find(
            (item) =>
              item?.properties?.[core.ADD_OPERATION_PROPERTY] === operationId
          );
        } catch {
          bookingLine = await recoverAmbiguousAdd(operationId, startedAt);
        }
      }

      await addMissingAddOns(form, linkedCart);
      const email = String(new FormData(form).get('properties[Email]') || '');
      await preserveBookingIntegrations(email, bookingLine, linkedCart);
      await reconcile();
      return bookingLine;
    }

    function desiredBookingFromForm(form, currentLine) {
      const item = formBookingItem(form);
      item.properties = {
        ...(currentLine.properties || {}),
        ...item.properties,
      };
      item.properties[core.LINKED_CART_PROPERTY] =
        form.dataset.linkedCartId ||
        currentLine.properties?.[core.LINKED_CART_PROPERTY];
      const payload = core.buildBookingAndFeePayload({
        bookingItem: item,
        cart: { items: [{ ...currentLine, ...core.FEE_LINE_PROPERTIES }] },
        eligibleProductIds,
        operationId:
          currentLine.properties?.[core.ADD_OPERATION_PROPERTY] ||
          form.dataset.swyshOperationId,
      });
      return payload.items.find(
        (candidate) => Number(candidate.id) === Number(item.id)
      );
    }

    function isEligibleBookingForm(form) {
      const item = formBookingItem(form);
      return core.classifyCart(
        {
          items: [
            {
              ...item,
              variant_id: item.id,
              key: form.dataset.variantId || 'candidate-booking',
            },
          ],
        },
        { eligibleProductIds }
      ).hasQualifier;
    }

    async function reconcileFormAddOns(form, linkedCart) {
      const desired = selectedAddOns(form, linkedCart);
      const desiredIds = new Set(desired.map((item) => item.id));
      let freshCart = await getCart();
      const current = (freshCart.items || []).filter(
        (item) =>
          item.properties?._Addon === 'Yes' &&
          item.properties?.[core.LINKED_CART_PROPERTY] === linkedCart
      );
      for (const line of current) {
        if (!desiredIds.has(Number(line.variant_id || line.id))) {
          await changeLine(line.key, 0);
          freshCart = await getCart();
        }
      }
      const currentIds = new Set(
        (freshCart.items || [])
          .filter(
            (item) =>
              item.properties?._Addon === 'Yes' &&
              item.properties?.[core.LINKED_CART_PROPERTY] === linkedCart
          )
          .map((item) => Number(item.variant_id || item.id))
      );
      const missing = desired.filter((item) => !currentIds.has(item.id));
      if (missing.length > 0) await addItems(missing);
    }

    async function restoreGroups(snapshot, linkedCartIds) {
      try {
        const current = await getCart();
        const currentGroupLines = (current.items || []).filter((item) =>
          linkedCartIds.has(
            String(item.properties?.[core.LINKED_CART_PROPERTY] || '')
          )
        );
        for (const line of currentGroupLines) {
          await changeLine(line.key, 0);
        }
        const originals = (snapshot.items || [])
          .filter((item) =>
            linkedCartIds.has(
              String(item.properties?.[core.LINKED_CART_PROPERTY] || '')
            )
          )
          .map((item) => ({
            id: Number(item.variant_id || item.id),
            quantity: Number(item.quantity) || 1,
            properties: { ...(item.properties || {}) },
          }));
        if (originals.length > 0) await addItems(originals);
        await reconcile();
      } catch {
        // Best-effort rollback deliberately does not expose cart or customer data.
      }
    }

    async function editCart(forms) {
      const eligibleForms = forms.filter(isEligibleBookingForm);
      const snapshot = await getCart();
      const linkedCartIds = new Set(
        eligibleForms
          .map((form) => String(form.dataset.linkedCartId || ''))
          .filter(Boolean)
      );
      try {
        for (const form of eligibleForms) {
          const freshCart = await getCart();
          const linkedCart = String(form.dataset.linkedCartId || '');
          const currentLine = (freshCart.items || []).find(
            (item) =>
              item.key === form.dataset.variantId ||
              (linkedCart &&
                item.properties?.[core.LINKED_CART_PROPERTY] === linkedCart &&
                item.properties?._Addon !== 'Yes')
          );
          if (!currentLine) continue;
          const desired = desiredBookingFromForm(form, currentLine);
          await changeLine(
            currentLine.key,
            Number(desired.quantity) || 1,
            desired.properties
          );
          await reconcileFormAddOns(form, linkedCart);
        }
        return await reconcile();
      } catch (error) {
        await restoreGroups(snapshot, linkedCartIds);
        throw error;
      }
    }

    async function removeLinkedCart(button) {
      const freshCart = await getCart();
      const linkedCart = String(button.dataset.linkedCartId || '');
      const key = button.dataset.variantId;
      const lines = (freshCart.items || []).filter(
        (item) =>
          item.key === key ||
          (linkedCart &&
            item.properties?.[core.LINKED_CART_PROPERTY] === linkedCart)
      );
      for (const line of lines) {
        const current = await getCart();
        const currentLine = (current.items || []).find(
          (item) =>
            item.key === line.key ||
            (linkedCart &&
              item.properties?.[core.LINKED_CART_PROPERTY] === linkedCart &&
              Number(item.variant_id || item.id) ===
                Number(line.variant_id || line.id))
        );
        if (currentLine) await changeLine(currentLine.key, 0);
      }
      return reconcile();
    }

    async function clearCart() {
      await requestJson(routes.clear, { method: 'POST' });
      return reconcile();
    }

    function navigate(url) {
      if (typeof window.__SwyshServiceFeeNavigateForTests === 'function') {
        window.__SwyshServiceFeeNavigateForTests(url);
      } else {
        window.location.assign(url);
      }
    }

    async function goToCheckout() {
      setCheckoutEnabled(false);
      try {
        const result = await reconcile();
        if (
          !result.classification?.isReconciled ||
          result.cart.items.length === 0
        ) {
          throw new Error('SERVICE_FEE_CHECKOUT_BLOCKED');
        }
        const email = String(result.cart.attributes?.email || '');
        const checkoutUrl = email
          ? `${routes.checkout}?checkout[email]=${encodeURIComponent(email)}`
          : routes.checkout;
        navigate(checkoutUrl);
        return result;
      } catch (error) {
        showError();
        throw error;
      }
    }

    async function runButtonAction(button, form, task) {
      clearError(form);
      setCheckoutEnabled(false);
      setButtonBusy(button, true);
      try {
        return await lock.run(button || form || 'service-fee', task);
      } catch (error) {
        showError(form);
        throw error;
      } finally {
        setButtonBusy(button, false);
      }
    }

    function handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const checkout = target?.closest('[data-swysh-checkout]');
      const bookingButton = target?.closest(
        'button.js-summary-book, button.js-product-book'
      );
      const editButton = target?.closest('button.js-update-book-video');
      const removeButton = target?.closest('.js-clear-cart-item');
      const clearButton = target?.closest('.js-clear-cart');

      if (checkout) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void goToCheckout().catch(() => undefined);
        return;
      }
      if (bookingButton) {
        const form = bookingButton.closest('form.book-product-form');
        if (!form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        void runButtonAction(bookingButton, form, async () => {
          await addBooking(form, bookingButton);
          navigate(routes.cartPage || '/cart');
        }).catch(() => undefined);
        return;
      }
      if (editButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const forms = Array.from(
          document.querySelectorAll('form.book-product-form.cart-form')
        );
        void runButtonAction(
          editButton,
          editButton.closest('form'),
          async () => {
            await editCart(forms);
            navigate(routes.cartPage || '/cart');
          }
        ).catch(() => undefined);
        return;
      }
      if (removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void runButtonAction(
          removeButton,
          removeButton.closest('form'),
          async () => {
            await removeLinkedCart(removeButton);
            navigate(routes.cartPage || '/cart');
          }
        ).catch(() => undefined);
        return;
      }
      if (clearButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void runButtonAction(
          clearButton,
          clearButton.closest('form'),
          async () => {
            await clearCart();
            navigate(routes.cartPage || '/cart');
          }
        ).catch(() => undefined);
      }
    }

    function handleSubmit(event) {
      const submitter = event.submitter;
      if (
        submitter instanceof HTMLElement &&
        submitter.matches('[name="checkout"][data-swysh-checkout]')
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void goToCheckout().catch(() => undefined);
        return;
      }

      const form =
        event.target instanceof HTMLFormElement ? event.target : undefined;
      if (form?.matches('form.book-product-form.product-form')) {
        const bookingButton =
          submitter instanceof HTMLButtonElement &&
          submitter.matches('button.js-summary-book, button.js-product-book')
            ? submitter
            : form.querySelector(
                'button.js-summary-book, button.js-product-book'
              );
        event.preventDefault();
        event.stopImmediatePropagation();
        void runButtonAction(bookingButton, form, async () => {
          await addBooking(form, bookingButton);
          navigate(routes.cartPage || '/cart');
        }).catch(() => undefined);
      }
    }

    function handleCartUpdated() {
      void reconcile()
        .then((result) => {
          setCheckoutEnabled(result.cart.items.length > 0);
          if (
            result.mutations > 0 &&
            /\/cart\/?$/.test(window.location.pathname)
          ) {
            navigate(routes.cartPage || '/cart');
          }
        })
        .catch(() => {
          setCheckoutEnabled(false);
          showError();
        });
    }

    let installed = false;

    function install() {
      if (window[INSTALLATION_KEY]) return;
      window[INSTALLATION_KEY] = true;
      installed = true;
      document.addEventListener('click', handleClick, true);
      document.addEventListener('submit', handleSubmit, true);
      document.addEventListener('swysh:cart-updated', handleCartUpdated);
    }

    function uninstall() {
      if (!installed) return;
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('submit', handleSubmit, true);
      document.removeEventListener('swysh:cart-updated', handleCartUpdated);
      installed = false;
      delete window[INSTALLATION_KEY];
    }

    async function initialize() {
      setCheckoutEnabled(false);
      const result = await reconcile();
      setCheckoutEnabled(result.cart.items.length > 0);
      return result;
    }

    return {
      addBooking,
      clearCart,
      editCart,
      getCart,
      goToCheckout,
      initialize,
      install,
      reconcile,
      removeLinkedCart,
      setCheckoutEnabled,
      uninstall,
    };
  }
})();
